import {
    Container, Dialog, DialogContent, Divider, Grid
} from '@material-ui/core';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import {
    BackspaceOutlined as BackspaceOutlinedIcon, Search as SearchIcon
} from '@material-ui/icons';
import MUIDataTable from "mui-datatables";
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import SelectSupplier from '../../components/Form/SelectSupplier';
import TextField from '../../components/Form/TextField';
import DateField from '../../components/Form/Date';
import ButtonUI from '../../components/ui/ButtonUI';
import LoadingUI from '../../components/ui/LoadingUI';
import PageTitle from '../../components/ui/PageTitleUI';
import { useAuth } from '../../hooks/auth';
import { MvmSearch } from '../../interfaces/Mvm';
import MvmStatus from '../../interfaces/MvmStatus';
import MvmApi from '../../services/MvmApi';
import getDefaultGridOptions from '../../utils/componentUtils';
import utilsDate from '../../utils/Date';
import permissions from '../../utils/Permissions';
import useKeypress from '../../utils/useKeypress';
import { DialogTitle, PaperComponent, useStyles } from './Style';
import WindowFiapeForm from './WindowFiapeForm';
import * as Yup from 'yup'

const DeliveryWindow: React.FC = () => {
    const classes = useStyles();
    const [mvms, setMvms] = useState<any>();
    const [loading, setLoading] = useState(true);
    const [openedPainel, setOpenedPainel] = React.useState(false);
    const [mvmId, setMvmId] = useState<number>(0);
    const [deliveryWindowDate, setDeliveryWindowDate] = useState<Date>(new Date());
    const { user, hasPermission } = useAuth();
    const { t } = useTranslation("WindowFiape");
    const { t: _t } = useTranslation("General");
    const { t: tVal } = useTranslation("Validation");
    const history = useHistory();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [count, setCount] = useState(0);
    const [sort] = useState<string>('id:desc');
    const { control, handleSubmit, getValues, reset, clearErrors, setError } = useForm<IFormInput>(
        {
            defaultValues: {
                mvmNumber: "",
                supplierIds: [],
                submitDateStart: null,
                submitDateEnd: null,
                vehiclePlateNumber: '',
                cartPlateNumber: ''
            }
        });

    interface IFormInput {
        mvmNumber: any;
        supplierIds: any;
        submitDateStart: Date | null;
        submitDateEnd: Date | null;
        vehiclePlateNumber: any;
        cartPlateNumber: any;
    }

    const columns = [
        {
            name: 'id',
            options: {
                display: false,
            }
        },
        {
            name: 'deleted',
            options: {
                display: false,
            }
        },
        {
            name: 'mvmNumber',
            label: String(t('mvmNumber')),
            options: {
                setCellProps: () => ({
                    align: "left",
                }),
            }
        },
        {
            name: 'submitDate',
            label: String(t('submitDate')),

            options: {
                setCellProps: () => ({
                    align: "left"
                }),
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return utilsDate.DateTime(value);
                }
            }
        },
        {
            name: 'supplierNames',
            label: String(t('suppliers')),
            options: {
                setCellProps: () => ({
                    align: "left"
                }),
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return tableMeta.rowData[5]
                        .map((x: any) => x.supplierCorporateName)
                        .join(', ');
                }
            }
        },
        {
            name: 'suppliers',
            options: { display: false }
        },
        {
            name: 'vehiclePlateNumber',
            label: String(t('vehiclePlateNumber')),
            options: {
                setCellProps: () => ({
                    align: "left"
                }),
            }
        },
        {
            name: 'cartPlateNumber',
            label: String(t('cartPlateNumber')),
            options: {
                setCellProps: () => ({
                    align: "left"
                }),
            }
        },
        {
            name: 'deliveryWindowDate',
            label: String(t('deliveryWindow')),
            options: {
                setCellProps: () => ({
                    align: "left"
                }),
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return utilsDate.DateTimeShort(value);
                }
            }
        },
        {
            name: 'updatedBy',
            label: String(t('updatedBy')),
            options: {
                setCellProps: () => ({
                    align: "left"
                }),
            }
        },
        {
            name: 'updatedAt',
            label: String(t('updatedAt')),
            options: {
                setCellProps: () => ({
                    align: "left"
                }),
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return utilsDate.DateTime(value);
                }
            }
        },
    ]

    useKeypress('Escape', () => {
        setOpenedPainel(false);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!hasPermission(permissions.DELIVERY_WINDOW.Fiape))
            history.replace('NoPermission');

        setMvms([]);
        setLoading(false);
    }, [history, user]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (loading !== false) {
            const finalData: MvmSearch = {
                companyId: Number(user.company.companyID),
                status: MvmStatus.FLAG_STATUS_PENDING_ENTRANCE,
                pageSize: rowsPerPage,
                pageIndex: page,
                sort: sort
            };

            onSearch(finalData);
        }
    }, [page, rowsPerPage, sort, user]);

    const formValidation = async (data: any) => {
        var result = false
        clearErrors();

        try {
            const schema = Yup.object().shape({
                submitDateStart: Yup.date().nullable().typeError(tVal("formatDate")),
                submitDateEnd: Yup.date().nullable().typeError(tVal("formatDate"))
            });

            await schema.validate(data, {
                abortEarly: false,
            });

            result = true
        }
        catch (err) {

            var errors: string[] = []

            if (err instanceof Yup.ValidationError) {
                err.inner.forEach(error => {

                    if (errors.filter(element => element.includes(error.message)).length === 0)
                        errors.push(error.message)

                    setError(error.path?.split('.')[0] as
                        "submitDateStart" | "submitDateEnd"
                        , { message: error.message });
                })
            }

            result = false;
        }

        return result
    }

    const onSearch = (data: any) => {
        setLoading(true);

        MvmApi.searchMvm(data)
            .then(result => {
                if (result && result.items) {
                    setMvms(result);
                    setPage(result.pageIndex);
                    setCount(result.totalCount);
                }
                else {
                    setMvms([]);
                }
            }).catch(() => {
                setMvms([]);
            }).finally(() => {
                setLoading(false);
            });
    }

    const onSubmit = async (data: any) => {
        var validForm = await formValidation(data)

        if (!validForm)
            return;

        data.companyId = Number(user.company.companyID);
        data.status = MvmStatus.FLAG_STATUS_PENDING_ENTRANCE;
        data.pageSize = rowsPerPage;
        data.pageIndex = page;
        data.sort = sort;
        data.supplierIds = data.supplierIds?.map((element: { code: any; }) => Number(element.code)).join(',');

        onSearch(data);
    }

    const cleanFilters = () => {
        reset();
    };

    const handleOpenPanelChange = (data: any) => {
        setMvmId(data.id);
        setDeliveryWindowDate(data.deliveryWindowDate);
        setOpenedPainel(true);
    };

    const callbackDialogHandle = () => {
        setOpenedPainel(false);
        onSubmit(getValues());
    }

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: mvms?.items ? 'pointer' : ''
                }
            }
        }
    })

    return (
        <Container maxWidth={false} component="main">
            <Dialog
                open={openedPainel}
                aria-labelledby="form-dialog-title"
                PaperComponent={PaperComponent}
                onClose={() => setOpenedPainel(false)}
                classes={{ root: classes.dialogCalendar }}
                fullWidth>

                <DialogTitle classes={{ root: classes.dialogTitle }}
                    id="draggable-dialog-title"
                    onClose={() => setOpenedPainel(false)}>
                    {t("deliveryWindowSelection")}
                </DialogTitle>
                <DialogContent>
                    <WindowFiapeForm mvmId={mvmId} deliveryWindowDate={deliveryWindowDate} clickHandlerCalbkack={callbackDialogHandle} clickHandlerClose={() => setOpenedPainel(false)} />
                </DialogContent>
            </Dialog>

            <PageTitle title={t("title")} />
            <div className={classes.rootContent}>
                <div className={classes.paper}>

                    <form translate={undefined} onSubmit={handleSubmit(onSubmit)} >

                        <Grid container spacing={1}>
                            <Grid item xs={12} style={{ padding: "0px", textAlign: 'right' }}>
                                <ButtonUI onClick={() => cleanFilters()} startIcon={<BackspaceOutlinedIcon />}>{t("clean")}</ButtonUI>
                                <ButtonUI type="submit" color="primary" startIcon={<SearchIcon />}>{t("search")}</ButtonUI>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: '10px' }} />

                        <Grid container spacing={1}>

                            <Grid item xs={1}>
                                <TextField control={control} label={t("mvmNumber")} name="mvmNumber" fullWidth />
                            </Grid>

                            <Grid item xs={2}>
                                <SelectSupplier control={control} label={t("shippingSupplierId")} name="supplierIds" multiple={true} fullWidth />
                            </Grid>

                            <Grid item xs={2}>
                                <DateField control={control} label={t("submitDateStart")} InputLabelProps={{ shrink: true }} name="submitDateStart" fullWidth />
                            </Grid>

                            <Grid item xs={2}>
                                <DateField control={control} label={t("submitDateEnd")} InputLabelProps={{ shrink: true }} name="submitDateEnd" fullWidth />
                            </Grid>

                            <Grid item xs={2}>
                                <TextField control={control} label={t("vehiclePlateNumber")} mask="aaa9*99" name="vehiclePlateNumber" fullWidth />
                            </Grid>

                            <Grid item xs={2}>
                                <TextField control={control} label={t("cartPlateNumber")} mask="aaa9*99" name="cartPlateNumber" fullWidth />
                            </Grid>

                        </Grid>
                    </form>
                    <br />
                    {
                        loading ? <LoadingUI /> :
                            <MuiThemeProvider theme={myTheme}>
                                <MUIDataTable
                                    title=""
                                    data={mvms?.items}
                                    columns={columns}

                                    options={{
                                        ...getDefaultGridOptions(_t),
                                        serverSide: true,
                                        pagination: mvms?.items ? true : false,
                                        sort: mvms?.items ? true : false,
                                        selectToolbarPlacement: "none",
                                        filter: false,
                                        search: false,
                                        download: false,
                                        print: false,
                                        viewColumns: false,
                                        selectableRows: 'none',
                                        page,
                                        count,
                                        rowsPerPage,
                                        onCellClick: (_colData, { colIndex, dataIndex }) => {
                                            if (colIndex !== 2 && mvms && mvms.items) {
                                                handleOpenPanelChange(mvms.items[dataIndex]);
                                            }
                                        },
                                        onChangePage: (pageIndex) => { setLoading(true); setPage(pageIndex) },
                                        onChangeRowsPerPage: (rowsPerPage) => { setLoading(true); setPage(0); setRowsPerPage(rowsPerPage) },
                                    }}
                                />
                            </MuiThemeProvider>
                    }
                </div>
            </div>
        </Container>
    )
}

export default DeliveryWindow;